import React, { useState } from 'react'
import AppBar from '@material-ui/core/AppBar'
import CloseIcon from '@material-ui/icons/Close'
import Box from '@material-ui/core/Box'
import Container from '@material-ui/core/Container'
import IconButton from '@material-ui/core/IconButton'
import Link from '@material-ui/core/Link'
import MenuIcon from '@material-ui/icons/Menu'
import Toolbar from '@material-ui/core/Toolbar'
import Typography from '@material-ui/core/Typography'
import FiberManualRecordIcon from '@material-ui/icons/FiberManualRecord'
import {
  makeStyles,
  createStyles,
  withStyles,
  Theme,
} from '@material-ui/core/styles'
import clsx from 'clsx'
import useLockBodyScroll from 'react-use/lib/useLockBodyScroll'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import SearchIcon from '@material-ui/icons/Search'

import NavigationGroupAccordion from '@talentinc/gatsby-theme-ecom/components/NavigationGroup/NavigationGroupAccordion'
import NavigationGroupLink from '@talentinc/gatsby-theme-ecom/components/NavigationGroup/NavigationGroupLink'
import NavigationGroupMenu from '@talentinc/gatsby-theme-ecom/components/NavigationGroup/NavigationGroupMenu'
import usePageData from '@talentinc/gatsby-theme-ecom/hooks/usePageData'
import ScrollProgress from '@talentinc/gatsby-theme-ecom/components/Post/ScrollProgress'
import usePageMeasurements from '@talentinc/gatsby-theme-ecom/hooks/usePageMeasurements'
import MiniSearchForm from '@talentinc/gatsby-theme-ecom/components/Search/MiniSearchForm'
import BlogNavigation from './BlogNavigation'
import { isBrowser } from '@talentinc/gatsby-theme-ecom/utils/env'

interface Props {
  showBlogNavigation?: boolean
}

/**
 * The ZipJob global header.
 */
const Header: React.FC<Props> = (props) => {
  const isMobile = useMediaQuery<Theme>((theme) => theme.breakpoints.down('sm'))

  // For SSR, default to the desktop component, as it is somewhat responsive,
  // though the content is wrong. The navigation button open should only work
  // after first render, at which point MobileHeader will win
  if (isMobile && isBrowser) {
    return <MobileHeader {...props} />
  }

  return <DesktopHeader {...props} />
}

const MobileHeader: React.FC<Props> = ({ showBlogNavigation }) => {
  const { header, brand } = usePageData()
  const classes = useStyles()
  const [open, setOpen] = useState(false)
  const [searchOpen, setSearchOpen] = useState(false)

  useLockBodyScroll(open)

  if (!header || !brand) {
    return null
  }

  const logo = header.logo?.file ?? brand?.logo?.file

  return (
    <AppBar
      component="header"
      elevation={0}
      position="sticky"
      color="inherit"
      className={clsx({
        [classes.appBar]: true,
        [classes.appBarOpen]: open,
      })}
      data-gtm-role="header"
    >
      <NavToolbar>
        <Container className={classes.logoMenuButtons} disableGutters>
          {logo && (
            <Link className={classes.logo} href="/">
              <img alt={brand.name} height="29" src={logo.url} width="75" />
            </Link>
          )}
          <Box className={classes.menuButton}>
            {showBlogNavigation && !open && (
              <IconButton
                aria-label={`${!searchOpen ? 'open' : 'close'} search`}
                onClick={() => setSearchOpen((o) => !o)}
              >
                <SearchIcon />
              </IconButton>
            )}
            <IconButton
              aria-label={open ? 'close' : 'menu'}
              color="inherit"
              onClick={() => setOpen(!open)}
            >
              {open ? <CloseIcon /> : <MenuIcon />}
            </IconButton>
          </Box>
        </Container>
        {open && (
          <>
            {showBlogNavigation && (
              <BlogNavigation
                className={clsx({
                  [classes.mobileBlogNavigation]: true,
                  [classes.mobileBlogNavigationOpen]: open,
                })}
              />
            )}
            {header.name !== 'ZipJob Landing Page Header' &&
              header?.navigation?.map((navGroup, i) => (
                <NavLinkContainer
                  // @ts-ignore
                  component="nav"
                  key={navGroup.title}
                  className={clsx({
                    [classes.ctaContainer]: i === 1,
                    [classes.ctaContainerOpen]: i === 1 && open,
                  })}
                  disableGutters
                >
                  {navGroup.links.map((link) => (
                    <NavigationGroupLink key={link.contentful_id} link={link} />
                  ))}
                </NavLinkContainer>
              ))}
            {header.name === 'ZipJob Landing Page Header' &&
              header?.navigation?.map((navGroup, i) => (
                <>
                  {navGroup.contentful_id === '4Lv4NeSKsHaUfFCncugN2c' ||
                  navGroup.contentful_id === '7pA705JaMNezfyp09zQyHo' ? (
                    <NavigationGroupAccordion navGroup={navGroup} />
                  ) : (
                    <NavLinkContainer
                      // @ts-ignore
                      component="nav"
                      key={navGroup.title}
                      className={clsx({
                        [classes.navLinkContainer]: true,
                        [classes.ctaContainer]: i === 1,
                        [classes.ctaContainerOpen]: i === 1 && open,
                        [classes.navButtonContainer]:
                          navGroup.contentful_id === '3zChumoV3iOPeTtCdldE6K',
                        [classes.navPhoneContainer]:
                          navGroup.contentful_id === '441BriWi1zKDtUuov47sKM',
                      })}
                      disableGutters
                    >
                      {navGroup.contentful_id === '441BriWi1zKDtUuov47sKM' && (
                        <FiberManualRecordIcon
                          className={classes.availableDot}
                        />
                      )}
                      {navGroup.links.map((link) => (
                        <>
                          {navGroup.contentful_id ===
                          '441BriWi1zKDtUuov47sKM' ? (
                            <Container
                              disableGutters
                              className={classes.navCallUs}
                            >
                              <Typography>Call us</Typography>
                              <NavigationGroupLink
                                key={link.contentful_id}
                                link={link}
                              />
                            </Container>
                          ) : (
                            <NavigationGroupLink
                              key={link.contentful_id}
                              link={link}
                            />
                          )}
                        </>
                      ))}
                    </NavLinkContainer>
                  )}
                </>
              ))}
          </>
        )}
      </NavToolbar>
      {searchOpen && (
        <Container className={classes.mobileSearchContainer}>
          <MiniSearchForm
            autoFocus
            onSubmit={() => setSearchOpen(false)}
            onClose={() => setSearchOpen(false)}
            classes={{
              input: classes.mobileSearchInput,
            }}
          />
        </Container>
      )}
    </AppBar>
  )
}

const DesktopHeader: React.FC<Props> = ({ showBlogNavigation }) => {
  const classes = useStyles()
  const { header, brand } = usePageData()
  const [open, setOpen] = useState(false)
  const {
    header: [headerRef],
  } = usePageMeasurements()

  // Lock the body scroll if the mobile nav is open.
  useLockBodyScroll(open)

  if (!header || !brand) {
    return null
  }

  const logo = header.logo?.file ?? brand?.logo?.file

  return (
    <AppBar
      className={clsx({
        [classes.appBar]: true,
        [classes.appBarOpen]: open,
      })}
      component="header"
      elevation={0}
      position="sticky"
      color="inherit"
      ref={headerRef}
      data-gtm-role="header"
    >
      <NavToolbar>
        <Container className={classes.logoMenuButtons} disableGutters>
          {logo && (
            <Link className={classes.logo} href="/">
              <img alt={brand.name} height="29" src={logo.url} width="75" />
            </Link>
          )}
          <IconButton
            aria-label={open ? 'close' : 'menu'}
            className={classes.menuButton}
            color="inherit"
            onClick={() => setOpen(!open)}
          >
            {open ? <CloseIcon /> : <MenuIcon />}
          </IconButton>
        </Container>
        <Container
          className={clsx({
            [classes.navLinks]: true,
            [classes.navLinksOpen]: open,
          })}
          disableGutters
        >
          {header.name !== 'ZipJob Landing Page Header' &&
            header?.navigation?.map((navGroup, i) => (
              <NavLinkContainer
                // @ts-ignore
                component="nav"
                key={navGroup.title}
                className={clsx({
                  [classes.ctaContainer]: i === 1,
                  [classes.ctaContainerOpen]: i === 1 && open,
                })}
                disableGutters
              >
                {navGroup.links.map((link) => (
                  <NavigationGroupLink key={link.contentful_id} link={link} />
                ))}
              </NavLinkContainer>
            ))}
          {header.name === 'ZipJob Landing Page Header' &&
            header?.navigation?.map((navGroup, i) => (
              <>
                {navGroup.contentful_id === '4Lv4NeSKsHaUfFCncugN2c' ||
                navGroup.contentful_id === '7pA705JaMNezfyp09zQyHo' ? (
                  <NavigationGroupMenu navGroup={navGroup} />
                ) : (
                  <NavLinkContainer
                    // @ts-ignore
                    component="nav"
                    key={navGroup.title}
                    className={clsx({
                      [classes.navLinkContainer]: true,
                      [classes.ctaContainer]: i === 1,
                      [classes.ctaContainerOpen]: i === 1 && open,
                      [classes.navButtonContainer]:
                        navGroup.contentful_id === '3zChumoV3iOPeTtCdldE6K',
                      [classes.navPhoneContainer]:
                        navGroup.contentful_id === '441BriWi1zKDtUuov47sKM',
                    })}
                    disableGutters
                  >
                    {navGroup.contentful_id === '441BriWi1zKDtUuov47sKM' && (
                      <FiberManualRecordIcon className={classes.availableDot} />
                    )}
                    {navGroup.links.map((link) => (
                      <NavigationGroupLink
                        key={link.contentful_id}
                        link={link}
                      />
                    ))}
                  </NavLinkContainer>
                )}
              </>
            ))}
        </Container>
      </NavToolbar>
      {showBlogNavigation && (
        <BlogNavigation className={classes.blogNavigation} />
      )}
      {!open && <ScrollProgress className={classes.ScrollProgress} />}
    </AppBar>
  )
}

export const NavToolbar = withStyles((theme) => ({
  root: {
    alignItems: 'center',
    display: 'flex',
    minHeight: 'initial',
    padding: '.78em 6% .65em 6%',

    '& a': {
      color: theme.palette.text.primary,
      textDecoration: 'none',
    },

    '& nav:last-child': {
      marginLeft: 'auto',
    },

    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
    },
    '@media (min-width: 1456px)': {
      padding:
        '.78em calc((100% - 91rem)/2 + 5.5rem) .65em calc((100% - 91rem)/2 + 5.5rem)',
    },
  },
}))(Toolbar)

export const NavLinkContainer = withStyles((theme) => ({
  root: {
    margin: 'initial',
    padding: '0',
    '& > a': {
      color: theme.palette.text.primary,
      fontSize: '.94rem',
      marginRight: '1.3em',
      whiteSpace: 'nowrap',
    },
    [theme.breakpoints.down('sm')]: {
      display: 'flex',
      flexDirection: 'column',
      margin: '.59em 0 1.64em',

      '& > a': {
        fontWeight: 'normal',
        padding: '.81em 0',
        margin: 0,
        fontSize: '1.125em',
        borderBottom: `1px solid ${
          theme.colors?.gray?.[100] ?? theme.palette.text.hint
        }`,
      },
    },
  },
}))(Container)

const useStyles = makeStyles((theme) =>
  createStyles({
    appBar: {
      backgroundColor: theme.palette.background.default,
      // @TODO Is this doing anything special?
      // height: '4.8em',
      transition: 'height ease-out .25s',

      [theme.breakpoints.down('sm')]: {
        overflow: 'scroll',
      },
    },
    appBarOpen: {
      [theme.breakpoints.down('sm')]: {
        height: '100vh',
      },
    },
    availableDot: {
      fill: theme.palette.success.main,
      fontSize: '1em',
      height: '1em',
      marginRight: '.5em',
      width: '1em',
    },
    ctaContainer: {
      display: 'flex',
      justifyContent: 'flex-end',
      margin: 'initial',
    },
    ctaContainerOpen: {
      justifyContent: 'space-around',
      '& > a': {
        width: '100%',
      },
    },
    logo: {
      display: 'flex',
      height: '1.81em',
      width: '4.69em',

      '& img': {
        height: '100%',
        width: '100%',
      },
    },
    logoMenuButtons: {
      margin: '0',
      width: 'auto',
      [theme.breakpoints.down('sm')]: {
        alignItems: 'center',
        display: 'flex',
        justifyContent: 'space-between',
        order: -1,
        width: '100%',
      },
    },
    menuButton: {
      display: 'none',
      [theme.breakpoints.down('sm')]: {
        display: 'initial',
        marginRight: '-0.75rem',
      },
    },
    navButtonContainer: {
      display: 'flex',
      justifyContent: 'flex-end',
      minWidth: '12em',
      [theme.breakpoints.down('sm')]: {
        margin: '1.81em 0',

        '& button': {
          width: '100%',
        },
      },
    },
    navCallUs: {
      alignItems: 'center',
      display: 'flex',
      margin: '0',
      width: 'unset',
    },
    navLinkContainer: {
      alignItems: 'center',
      display: 'flex',

      [theme.breakpoints.down('sm')]: {
        '& > a': {
          padding: '1.11em 0',
        },
      },
    },
    navLinks: {
      alignItems: 'center',
      display: 'flex',
      justifyContent: 'space-between',
      margin: '0 0 0 4.48em',
      width: '100%',
      maxWidth: '100%',

      [theme.breakpoints.down('sm')]: {
        display: 'none',
      },
    },
    navLinksOpen: {
      [theme.breakpoints.down('sm')]: {
        display: 'flex',
        flexDirection: 'column',
        margin: '0',
      },
    },
    navPhoneContainer: {
      justifyContent: 'flex-end',
      [theme.breakpoints.down('sm')]: {
        borderBottom: `1px solid ${theme.colors.gray[100]}`,
        flexDirection: 'row-reverse',
        justifyContent: 'space-between',
        margin: '0',
        order: -1,
        padding: '1.25em 0',
        width: '100%',
      },
    },
    blogNavigation: {
      [theme.breakpoints.up('sm')]: {
        borderTop: `1px solid ${
          theme.colors?.gray?.[100] ?? theme.palette.text.hint
        }`,
      },
      [theme.breakpoints.down('sm')]: {
        display: 'none',
      },
    },
    mobileBlogNavigation: {
      display: 'none',
      width: '100%',
    },
    mobileBlogNavigationOpen: {
      display: 'flex',
      borderBottom: `1px solid ${theme.colors.gray[100]}`,
    },
    mobileSearchContainer: {
      borderTop: `4px solid ${
        theme.colors?.gray?.[100] ?? theme.palette.text.hint
      }`,
      paddingRight: '2.5em',
    },
    mobileSearchInput: {
      width: '100%',
      height: '2.5995575221em',
    },
    ScrollProgress: {
      backgroundColor: theme.colors?.gray?.[100] ?? theme.palette.text.hint,
    },
  })
)

export default Header
